import {
    CompetitorMediaEffectQueryArgs,
    RowValueType,
} from '@/dashboards/CompetitorsMediaInvestment/types';
import { parseCompetitorVariable } from '@/dashboards/CompetitorsMediaInvestment/helpers/parseCompetitorVariable';
import {
    TCompetitorMediaInvestmentTableRow,
    TDescribeVariablesTableRow,
} from '@/duckdb/competitor-media-investment/schema';
import {
    ModelContributionsTable,
    TModelContributionRow,
} from '@/duckdb/model-contributions';
import { duckDB } from '@analytical-alley/duckdb-react';
import moment from 'moment';
import { CompetitorMediaInvestmentTable } from './index';
import { partitionExistingColumns } from './partitionExistingColumns';

export type CompetitorInvestmentRowsResult = {
    rows: RowValueType;
    /**
     * Requested competitor variables with no matching column in the input file.
     * Surfaced to the UI so the discrepancy is visible rather than silently
     * dropped — see `formatMissingVariablesNotice`.
     */
    missingVariables: string[];
};

async function getCompetitorInvestmentRows(
    competitorVariables: string[],
): Promise<CompetitorInvestmentRowsResult> {
    if (!competitorVariables.length) {
        return { rows: [], missingVariables: [] };
    }

    // Mirrors `getOwnInvestmentRows` below: never interpolate a column name
    // into a SELECT without confirming it exists, or DuckDB raises a
    // Binder Error that takes down the whole route (AAPD-678).
    const variableListQuery = CompetitorMediaInvestmentTable.rawQuery<
        () => TDescribeVariablesTableRow[]
    >(
        () =>
            `SELECT column_name
             FROM (DESCRIBE "DATA")
             WHERE column_name IN ( '${competitorVariables.join("','")}');`,
    );
    const variableListQueryRes = await duckDB.query(variableListQuery);

    const { present, missing } = partitionExistingColumns(
        competitorVariables,
        variableListQueryRes.map((row) => row.column_name),
    );

    if (!present.length) {
        return { rows: [], missingVariables: missing };
    }

    const query = CompetitorMediaInvestmentTable.rawQuery<
        () => TCompetitorMediaInvestmentTableRow[]
    >(
        () =>
            `SELECT "Period Name" AS "periodName", "${present.join('","')}" FROM "DATA";`,
    );

    const queryRes = await duckDB.query(query);

    const rows = queryRes
        .map((row) => {
            const period: string = row.periodName ?? row['Period Name'];
            const res: RowValueType = [];

            for (const key in row) {
                if (key === 'periodName' || key === 'Period Name') {
                    continue;
                }

                const parsed = parseCompetitorVariable(key);
                const variable = parsed?.name ?? key;
                const category = parsed?.channel ?? '';

                const item = {
                    period: {
                        value: {
                            raw: period,
                            formatted: moment.utc(period).format('DD-MM-YYYY'),
                        },
                    },
                    period_name_day: {
                        value: {
                            raw: period,
                            formatted: moment.utc(period).format('DD-MM-YYYY'),
                        },
                    },
                    investment: {
                        value: {
                            raw: row[key] ?? 0,
                            formatted: (row[key] ?? 0).toString(),
                        },
                    },
                    variable: {
                        value: { raw: variable!, formatted: variable!.trim() },
                    },
                    category: {
                        value: { raw: category!, formatted: category!.trim() },
                    },
                };

                res.push(item);
            }

            return res;
        })
        .flat();

    return { rows, missingVariables: missing };
}

async function getOwnInvestmentRows(
    variableCategories: string[],
): Promise<RowValueType> {
    const variableListQuery = CompetitorMediaInvestmentTable.rawQuery<
        () => TDescribeVariablesTableRow[]
    >(
        () =>
            `SELECT column_name
             FROM (DESCRIBE "DATA")
             WHERE column_name IN ( '${variableCategories.join("','")}');`,
    );
    const variableListQueryRes = await duckDB.query(variableListQuery);
    const existingVariables = variableListQueryRes.map(
        (row) => row.column_name,
    );

    const query = CompetitorMediaInvestmentTable.rawQuery<
        () => TCompetitorMediaInvestmentTableRow[]
    >(
        () =>
            `SELECT "Period Name", "${existingVariables.join('","')}" FROM "DATA";`,
    );

    const queryRes = await duckDB.query(query);

    return queryRes
        .map((row) => {
            const period: string = row['Period Name'];
            const res: RowValueType = [];

            for (const key in row) {
                if (key === 'Period Name') {
                    continue;
                }

                const item = {
                    period: {
                        value: {
                            raw: period,
                            formatted: moment.utc(period).format('DD-MM-YYYY'),
                        },
                    },
                    period_name_day: {
                        value: {
                            raw: period,
                            formatted: moment.utc(period).format('DD-MM-YYYY'),
                        },
                    },
                    investment: {
                        value: {
                            raw: row[key],
                            formatted: row[key].toString(),
                        },
                    },
                    variable: {
                        value: {
                            raw: 'Your Investment',
                            formatted: 'Your Investment',
                        },
                    },
                    category: {
                        value: { raw: key, formatted: key.trim() },
                    },
                };

                res.push(item);
            }

            return res;
        })
        .flat();
}

async function getCompetitorMediaEffectRows({
    competitorImpactVariables,
    period,
    startDate,
    endDate,
}: CompetitorMediaEffectQueryArgs): Promise<RowValueType> {
    const query = ModelContributionsTable.rawQuery<
        () => TModelContributionRow[]
    >(
        ({ table }) =>
            `SELECT "Period name", "variable", "contribution" FROM "${table}"('${period}', ${startDate ? `'${startDate}'` : 'NULL'}, ${endDate ? `'${endDate}'` : 'NULL'}) WHERE "variable" IN ('${competitorImpactVariables.join("','")}');`,
    );
    const queryRes = await duckDB.query(query);

    return queryRes.map((row) => {
        const parsed = parseCompetitorVariable(row.variable);
        const variable = parsed?.name ?? row.variable;
        const category = parsed?.channel ?? '';
        return {
            variable: {
                value: { raw: variable!, formatted: variable!.trim() },
            },
            category: {
                value: { raw: category!, formatted: category!.trim() },
            },
            investment: {
                value: {
                    raw: row.contribution,
                    formatted: row.contribution?.toString(),
                },
            },
            period: {
                value: {
                    raw: row.periodName,
                    formatted: row.periodName,
                },
            },
            period_name_day: {
                value: {
                    raw: row.periodName,
                    formatted: row.periodName,
                },
            },
        };
    });
}

export const competitorMediaInvestmentQueries = {
    getCompetitorInvestmentRows,
    getOwnInvestmentRows,
    getCompetitorMediaEffectRows,
};
